import type { FC, FunctionComponent } from 'react';
import type { Control, FieldValues } from 'react-hook-form';
import type { AnySchema, Lazy, Reference } from 'yup';
import type {
  JsonDataWrapper,
  OdinDataRetrieval,
  OdinDataUpdate,
} from '../../common/data-retrieval/data-retrieval-interfaces';
import type { OdinDataSender } from '../../common/data-retrieval/odin-data-sender';
import type { OdinKey } from '../../common/types/odin-key.type';
import type { ButtonHtmlType, ButtonType, ButtonVariant } from '../button/button';
import type { JsonData, JsonDataItem } from '../generic-types';
import type { DynamicFormRequiredType } from './dynamic-form.enums';
import type { VisibilityConditionConfig } from './visibility-conditions/visibility-condition.interfaces';

export type DynamicFormFieldType =
  | 'LABEL'
  | 'TEXTFIELD'
  | 'TEXTAREA'
  | 'RICHTEXTAREA'
  | 'CHECKBOX'
  | 'DATEFIELD'
  | 'TIMEFIELD'
  | 'COMBOBOX'
  | 'LIST_BOX'
  | 'OPTIONGROUP'
  | 'INTFIELD'
  | 'DOUBLEFIELD'
  | 'HISTORY'
  | 'SIGNATURECAPTURE'
  | 'MULTISELECTCHECKBOX'
  | 'MULTISELECTFIELD'
  | 'GRAND_SCORE'
  | 'GROUP'
  | 'DROPDOWNMULTISELECT'
  | 'TWIN_COLUMN_SELECT'
  | 'DATEFIELD_COMPETENCY'
  | 'OPTION_BUILDER'
  | 'ICON_SELECTOR'
  | 'CUSTOM';

export interface DynamicFormFieldPermissions {
  read?: boolean;
  edit?: boolean;
}

export interface DynamicFormButtonSetting {
  name: string;
  text: string;
  type?: ButtonType;
  variant?: ButtonVariant;
  htmlType: ButtonHtmlType;
  id: OdinKey;
  className?: string;
  hidden?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  /**
   * Controls how this button is placed in the mobile layout.
   * - `'standalone'`: rendered outside the grouped workflow actions (always visible on its own).
   * - `'group'` (default): participates in the popover / inline workflow group.
   *
   * When omitted, the component falls back to the `name === 'save'` heuristic.
   */
  mobilePlacement?: 'standalone' | 'group';
  /**
   * Used in visibility conditions
   */
  isVisibleForced?: boolean;
}

export interface DynamicFormError {
  message: string;
}

export const DynamicFormButtonLocation = {
  Left: 0,
  Right: 1,
} as const;
export type DynamicFormButtonLocation = (typeof DynamicFormButtonLocation)[keyof typeof DynamicFormButtonLocation];

export const DynamicFormButtonPosition = {
  Inline: 0,
  Fixed: 1,
} as const;
export type DynamicFormButtonPosition = (typeof DynamicFormButtonPosition)[keyof typeof DynamicFormButtonPosition];

// I'm not allowing any's in the library going forward, but in these cases they are needed since using unknown will
// require the components to deal with casting their value and onChange types, so they can be added to the dynamic field
// service which we do not want because that is leaking implementation knowledge into the field components.
export type FieldComponent<T> = FunctionComponent<DynamicFieldComponentProps<T> & DynamicFieldDataProps>;

export type FieldComponents<T> = Record<string, FieldComponent<T>>;

export type HfFieldComponent<T> = FunctionComponent<HfDynamicFieldComponentProps<T, any> & DynamicFieldDataProps>;

export type HfFieldComponents<T> = Record<string, HfFieldComponent<T>>;

export type FieldValueConverter = (value: unknown) => unknown;

export interface DynamicFormSettings {
  id: OdinKey;
  caption: string;
  fields: Array<DynamicFormField>;
  customFieldComponents?: Record<string, FC<any>>;
  visibilityConditions?: Array<VisibilityConditionConfig>;
  fieldValueConverters?: Record<string, FieldValueConverter>;
  userGroups?: JsonData;
  globalProperties?: JsonDataItem;
  workflowStepVisibilityConditions?: Array<VisibilityConditionConfig>;
  errors?: Record<string, ErrorsObjectTypes>;
}

export interface ErrorsObjectTypes {
  message?: string;
  type?: string;
  ref?: unknown;
}

export type DynamicFieldDataTypes = JsonData | (OdinDataRetrieval & OdinDataUpdate<JsonDataWrapper>);

export interface DynamicFieldDataProps {
  textField: string;
  valueField: string;
  archivedField?: string;
  /**
   * This needs to be allowed to be undefined in the case of passing JsonData; in that case the data usually starts
   * undefined.
   */
  data?: DynamicFieldDataTypes;
  pageSize?: number;
  showAddNewOption?: boolean;
}

export interface DynamicFormField {
  id: OdinKey;
  /**
   * When set this field will re-fetch its data when the parent field changes.
   */
  parentFieldId?: string;
  fieldType: DynamicFormFieldType;
  /**
   * If the fieldType is CUSTOM then this will determine the custom field to use.
   */
  customFieldType?: string;
  fieldName: string;
  caption: string;
  position: number;
  startOpen?: boolean;
  required?: DynamicFormRequiredType;
  fields?: Array<DynamicFormField>;
  dataSettings?: DynamicFieldDataProps;
  dynamicProperties?: JsonDataItem;
  helpMessage?: string;
  hintMessage?: string;
  fieldData?: string;
  permissions?: DynamicFormFieldPermissions;
  hidden?: boolean;
  /**
   * For custom components setting this property will wrap the component in its own group.
   */
  wrapInGroup?: boolean;
  /**
   * For custom components this will be the title on the group.
   */
  customGroupTitle?: string;
  validationFunc?: AnySchema | Reference | Lazy<AnySchema, unknown>;
  childWrapperStyles?: string;
  labelStyles?: string;
  importEnabled?: boolean;
  lockDown?: boolean;
  /**
   * Will force the field to be either visible or hidden regardless of visibility conditions.
   */
  isVisibleForced?: boolean;
}

export interface DynamicFieldComponentProps<V, C = undefined> {
  id?: OdinKey;
  name?: string;
  label?: string;
  placeholder?: string;
  error?: string;
  value?: V;
  onChange?: (value?: C extends undefined ? V : C) => void;
  onFocus?: () => void;
  readOnly?: boolean;
  required?: DynamicFormRequiredType;
  permissions?: DynamicFormFieldPermissions;
  dataRetrieval?: OdinDataRetrieval;
  inputRef?: (instance: unknown) => void;
  registerPreSubmitHandler?: RegisterPreSubmitFunction;
  registerPostSubmitHandler?: RegisterPostSubmitFunction;
  fieldValueConverter?: FieldValueConverter;
  disabled?: boolean;
  className?: string;
  labelStyles?: string;
  enableDebounce?: boolean;
}

export interface HfDynamicFieldComponentProps<V, C = undefined, TFieldValues extends FieldValues = FieldValues>
  extends DynamicFieldComponentProps<V, C> {
  control: Control<TFieldValues>;
  name: string;
}

export interface DynamicFormParentFieldValue {
  parentFieldId: string;
  fieldValue: unknown;
  type?: string;
  initialValue?: boolean;
}

export type FieldChangedFunction = (
  fieldId: OdinKey,
  fieldName: string,
  fieldType: DynamicFormFieldType,
  fieldValue: unknown,
  customFieldType?: string
) => void;

export interface IDynamicFieldService {
  overrideFieldConfig(fieldConfig: FieldComponents<any> | HfFieldComponents<any>): void;

  getFieldComponent(fieldType: DynamicFormFieldType, customFieldType?: string): FC<any> | undefined;
}

export type RegisterPreSubmitFunction = (callback: (sender: OdinDataSender<JsonDataItem>) => void) => void;
export type RegisterPostSubmitFunction = (callback: (id: OdinKey) => (sender: OdinDataSender<void>) => void) => void;

export type PreSubmitHandler = (preSubmitData: JsonData, formData: JsonDataItem) => JsonDataItem;
export type PostSubmitHandler = (id?: OdinKey) => void;
